import { Link } from "react-router"
import type { Locale } from "../content/types"
import { getPagePath } from "../content/registry"
import { SITE_URL } from "../content/site"
import { ui } from "../content/ui"
import JsonLd from "./seo/JsonLd"
import { buildBreadcrumbSchema } from "../seo/schema/breadcrumb"

export interface Crumb {
  label: string
  path?: string
}

interface BreadcrumbsProps {
  locale: Locale
  items: Crumb[]
}

/**
 * Visible breadcrumb trail + matching JSON-LD BreadcrumbList.
 * The trail always starts at Home; nested pages append their parents.
 */
export default function Breadcrumbs({ locale, items }: BreadcrumbsProps) {
  const homePath = getPagePath("home", locale)!
  const crumbs: Crumb[] = [{ label: ui.breadcrumbs.home[locale], path: homePath }, ...items]
  const last = crumbs.length - 1

  return (
    <>
      <JsonLd
        data={buildBreadcrumbSchema(
          crumbs.map((c) => ({
            name: c.label,
            ...(c.path ? { path: `${SITE_URL}${c.path}` } : {}),
          })),
        )}
      />
      <nav aria-label={ui.breadcrumbs.ariaLabel[locale]} className="text-sm text-text-secondary">
        <ol className="flex flex-wrap items-center gap-2">
          {crumbs.map((crumb, index) => (
            <li key={`${crumb.label}-${index}`} className="flex items-center gap-2">
              {index > 0 && <span aria-hidden="true" className="text-text-muted">/</span>}
              {index === last || !crumb.path ? (
                <span aria-current={index === last ? "page" : undefined} className="text-text-secondary">
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.path} className="hover:text-button-primary transition-colors">
                  {crumb.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  )
}
